// A2F-764: right-pane wrapper. Sticky on ≥1280px (desktop-first per
// ticket); collapses to non-sticky below the breakpoint via the
// outer ProV2Page grid (this component itself is layout-agnostic).
//
// Composes: faux Word ribbon (top) + scrollable document body. The
// "LIVE PREVIEW · {section}" pill fades in the top-right while a
// section is focused and fades out after blur. Internal scroll on
// the doc area means a long sample can't push the ribbon off-screen.

import { useEffect, useState } from 'react'
import type { FormattingFields } from '../../data/onboard-v2-fields'
import type { TableStylePreset } from '../../types'
import type { DocSample } from '../../data/prov2-samples'
import { FauxWordRibbon, type RibbonButtonId } from './FauxWordRibbon'
import { DocumentSampleV2 } from './DocumentSampleV2'

interface FauxWordPreviewProps {
  sample: DocSample
  state: FormattingFields
  tableStyle: TableStylePreset
  packsActive: { coverLetter?: boolean; indSectionHeadings?: boolean }
  pulseId: RibbonButtonId | null
  /** Section currently focused — drives the LIVE PREVIEW pill copy. */
  focusedSection: string | null
}

const SECTION_LABEL: Record<string, string> = {
  doctype: 'Doc type',
  formatting: 'Formatting',
  tables: 'Table styles',
  packs: 'Packs',
  rules: 'Custom rules',
  install: 'Save & install',
}

export function FauxWordPreview({
  sample,
  state,
  tableStyle,
  packsActive,
  pulseId,
  focusedSection,
}: FauxWordPreviewProps) {
  // Pill visibility: shown while focusedSection is non-null; on blur
  // (focusedSection → null) we hold the pill for ~1s before fading
  // out, so a click that briefly defocuses (e.g. tabbing across
  // inputs) doesn't strobe the pill.
  const [pillSection, setPillSection] = useState<string | null>(null)
  useEffect(() => {
    if (focusedSection) {
      setPillSection(focusedSection)
      return
    }
    const t = setTimeout(() => setPillSection(null), 800)
    return () => clearTimeout(t)
  }, [focusedSection])
  const pillVisible = focusedSection !== null

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden flex flex-col h-full">
      <FauxWordRibbon
        pulseId={pulseId}
        indSectionsActive={Boolean(packsActive.indSectionHeadings)}
        filename={sample.filename}
      />
      <div className="relative flex-1 overflow-y-auto bg-[#ECECEC] py-6">
        <DocumentSampleV2
          sample={sample}
          state={state}
          tableStyle={tableStyle}
          packsActive={packsActive}
        />
        <div
          aria-hidden
          className={`pointer-events-none absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-1 bg-brand-orange text-white shadow-sm transition-opacity duration-300 ${
            pillVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          Live preview · {pillSection ? SECTION_LABEL[pillSection] ?? pillSection : 'idle'}
        </div>
      </div>
    </div>
  )
}
